import { COMPANY_NAME, type ParticipantNote } from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, FancyDate, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaEye, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { LabelsStack } from '../../components/generic/LabelsStack';
import { ListItem } from '../../components/generic/ListItem';
import { showModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { UserAvatar } from '../../components/user/UserAvatar';
import { useCreateParticipantNoteMutation } from '../../graphql/hooks/useCreateParticipantNoteMutation';
import { useParticipantNotesQuery } from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantNoteActionMenu } from '../notes/ParticipantNoteActionMenu';
import { ParticipantNoteCreateModal } from '../notes/ParticipantNoteCreateModal';
import { ParticipantNoteEditModal } from '../notes/ParticipantNoteEditModal';

interface Props {
  participantId: string;
  isDisabled?: boolean;
}

export const ParticipantNotesCard = (props: Props) => {
  const { participantId, isDisabled } = props;
  const { organization, user, successToast, errorToast } = useAppContext();
  const navigate = useNavigate();

  const { isLoading, error, data } = useParticipantNotesQuery({
    organizationId: organization.id,
    participantId,
  });

  const mutation = useCreateParticipantNoteMutation();

  const onViewAllNotes = React.useCallback(() => {
    void navigate(`/organizations/${organization.id}/participants/${participantId}/notes`);
  }, [navigate, organization.id, participantId]);

  const onAddNoteFromTemplate = React.useCallback(() => {
    showModal(ParticipantNoteCreateModal, { participantId });
  }, [participantId]);

  const onAddBlankNote = React.useCallback(async () => {
    await mutation
      .mutateAsync({
        organizationId: organization.id,
        request: {
          id: undefined,
          participantId: participantId,
          userId: user.id,
          labelIds: [],
        },
      })
      .then((res) => {
        successToast('Note created successfully');
        showModal(ParticipantNoteEditModal, {
          participantId,
          participantNoteId: res.createParticipantNote.id,
          isDisabled,
        });
      })
      .catch((err) => {
        errorToast(`Error creating note: ${err.message}`);
      });
  }, [errorToast, isDisabled, mutation, organization.id, participantId, successToast, user.id]);

  const participantNotes =
    (data?.organization?.participant?.notes.results as ParticipantNote[]) ?? [];
  const recentParticipantNotes = P.take(participantNotes, 3);

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError h='full' error={error} />;
    }

    if (P.isEmpty(recentParticipantNotes)) {
      return <Empty h='full' title='No notes recorded yet' p={0} />;
    }

    return (
      <ChakraV3.Stack gap={6}>
        {recentParticipantNotes.map((participantNote) => {
          return (
            <ListItem
              key={participantNote.id}
              icon={
                <UserAvatar
                  secondary={participantNote.user?.email ?? 'admin@piccolohealth.com'}
                  name={participantNote.user?.name ?? COMPANY_NAME}
                  picture={participantNote.user?.picture}
                  h={10}
                  w={10}
                />
              }
              title={participantNote.user?.name ?? COMPANY_NAME}
              secondary={
                <FancyDate
                  date={participantNote.createdAt}
                  showHumanized={false}
                  color='secondary'
                  fontSize='xs'
                />
              }
              tertiary={<LabelsStack labels={participantNote.labels} />}
              right={
                <ChakraV3.HStack>
                  <ChakraV3.Button
                    size='sm'
                    variant='plain'
                    w='fit-content'
                    onClick={() =>
                      showModal(ParticipantNoteEditModal, {
                        participantNoteId: participantNote.id,
                        participantId: participantId,
                        isDisabled,
                      })
                    }
                  >
                    View
                  </ChakraV3.Button>
                  <ParticipantNoteActionMenu
                    participantNote={participantNote}
                    disabled={isDisabled}
                    variant='plain'
                    size='sm'
                  />
                </ChakraV3.HStack>
              }
            />
          );
        })}
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Heading size='xl' fontWeight='bold'>
            Notes
          </ChakraV3.Heading>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Recent notes
          </ChakraV3.Text>
        </ChakraV3.Stack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body as={ChakraV3.Stack} gap={6}>
        {content}
        <ChakraV3.Stack gap={4}>
          <ChakraV3.HStack>
            <ChakraV3.Button
              onClick={onAddNoteFromTemplate}
              flex={1}
              size='sm'
              disabled={isDisabled}
              variant='subtle'
            >
              <FaPlus />
              Add note from template
            </ChakraV3.Button>
            <ChakraV3.Button
              onClick={onAddBlankNote}
              flex={1}
              size='sm'
              disabled={isDisabled}
              variant='subtle'
            >
              <FaPlus />
              Add blank note
            </ChakraV3.Button>
          </ChakraV3.HStack>
          <ChakraV3.Button onClick={onViewAllNotes} w='full' size='sm' variant='subtle'>
            <FaEye />
            View all
          </ChakraV3.Button>
        </ChakraV3.Stack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
